/**
 * DNS records sub-panel, nested inside the "Your own domain" option body —
 * not a page-level sibling card (the whole point of this revision). Keeps
 * the same band shape (header → intro → table → footer) across every
 * state; only the badge/rows/footer copy change.
 *
 * No "last checked" timestamp in the header — no backend field backs it
 * (org.modified changes on unrelated edits too), dropped rather than
 * fabricated, per explicit product decision.
 */
import React from "react";
import Badge from "../../../theme/untitled/Badge/Badge";
import Button from "../../../theme/untitled/Button/Button";
import Icon from "../../../theme/untitled/icons/Icon";
import IconButton from "../../../theme/untitled/IconButton/IconButton";
import CopyButton from "./CopyButton";
import FieldError from "./FieldError";
import { parseGeneralError } from "./errorHelpers";
import styles from "./DnsPanel.module.scss";

const RecordStatus = ({ status, error, onRegenerate, isRegenerating }) => {
  if (status === "success") {
    return (
      <Badge color="success" size="sm" dot>
        Verified
      </Badge>
    );
  }

  if (status === "error") {
    return (
      <div className={styles.statusStack}>
        <Badge color="error" size="sm" dot>
          Error
        </Badge>
        <div className={styles.statusReasonRow}>
          {error && <span className={styles.statusReason}>{error}</span>}
          {onRegenerate && (
            <button
              type="button"
              className={styles.regenerateBtn}
              onClick={onRegenerate}
              disabled={isRegenerating}
            >
              {isRegenerating ? "Regenerating…" : "Regenerate"}
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Badge color="warning" size="sm" dot>
      Pending
    </Badge>
  );
};

const RecordRow = ({ type, name, value, status, error, onRegenerate, isRegenerating }) => (
  <tr>
    <td className={styles.typeCell} data-label="Type">
      {type}
    </td>
    <td className={styles.valueCell} data-label="Name">
      <span className={styles.mono}>{name}</span>
      <CopyButton value={name} />
    </td>
    <td className={styles.valueCell} data-label="Value">
      <span className={styles.mono}>{value}</span>
      <CopyButton value={value} />
    </td>
    <td data-label="Status">
      <RecordStatus
        status={status}
        error={error}
        onRegenerate={onRegenerate}
        isRegenerating={isRegenerating}
      />
    </td>
  </tr>
);

const DnsPanel = ({
  domain,
  dnsSettings,
  isRefreshing,
  isRegenerating,
  isDisconnecting,
  onRefreshDnsStatus,
  onRegenerate,
  onRequestDisconnect,
  refreshError,
  regenerateError,
  disconnectError,
}) => {
  const isAllVerified =
    dnsSettings.validationCNameStatus === "success" &&
    dnsSettings.routingCNameStatus === "success";

  const checkFailureMessage = refreshError
    ? parseGeneralError(refreshError, "The service didn't respond. Your records are unchanged.")
    : null;

  const regenerateErrorMessage = regenerateError
    ? parseGeneralError(regenerateError, "The service is unavailable. Try again in a moment.")
    : null;
  const disconnectErrorMessage = disconnectError
    ? parseGeneralError(disconnectError, "The service is unavailable. Try again in a moment.")
    : null;

  const disconnectButton = (
    <button
      type="button"
      className={styles.disconnectBtn}
      onClick={onRequestDisconnect}
      disabled={isDisconnecting}
    >
      {isDisconnecting ? "Disconnecting…" : "Disconnect domain"}
    </button>
  );

  return (
    <div className={styles.panel}>
      <div className={styles.band}>
        <span className={styles.title}>DNS records</span>
        {isAllVerified ? (
          <Badge color="success" size="sm" dot>
            Verified
          </Badge>
        ) : (
          <Badge color="warning" size="sm" dot>
            Pending verification
          </Badge>
        )}
        <div className={styles.headerActions}>
          {!isAllVerified && (
            <>
              <Button
                size="sm"
                hierarchy="secondaryGray"
                className={styles.checkButtonWide}
                iconLeading={<Icon name="RefreshCw02" size={16} />}
                onClick={onRefreshDnsStatus}
                disabled={isRefreshing}
                loading={isRefreshing}
              >
                {isRefreshing ? "Checking…" : "Check records"}
              </Button>
              <IconButton
                size="sm"
                variant="outline"
                className={styles.checkButtonCompact}
                icon={<Icon name="RefreshCw02" size={16} />}
                label={isRefreshing ? "Checking…" : "Check records"}
                onClick={onRefreshDnsStatus}
                disabled={isRefreshing}
                aria-busy={isRefreshing || undefined}
              />
            </>
          )}
        </div>
      </div>

      <div className={styles.band}>
        <p className={styles.intro}>
          {isAllVerified ? (
            <>
              Keep both records in place. Removing either one takes your links offline at{" "}
              <strong className={styles.introMono}>{domain}</strong>.
            </>
          ) : (
            "Add both records at your DNS provider — they will not affect your existing website or email. Verification is automatic once both are detected."
          )}
        </p>
      </div>

      {(regenerateErrorMessage || disconnectErrorMessage) && (
        <div className={styles.band}>
          <FieldError message={regenerateErrorMessage || disconnectErrorMessage} />
        </div>
      )}

      <div className={styles.tableBand}>
        <table className={styles.table}>
          <colgroup>
            <col className={styles.colType} />
            <col />
            <col />
            <col className={styles.colStatus} />
          </colgroup>
          <thead>
            <tr>
              <th>Type</th>
              <th>Name</th>
              <th>Value</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            <RecordRow
              type="CNAME"
              name={dnsSettings.validationCName}
              value={dnsSettings.validationCNameValue}
              status={dnsSettings.validationCNameStatus}
              error={dnsSettings.validationCNameError}
              onRegenerate={dnsSettings.validationCNameStatus === "error" ? onRegenerate : null}
              isRegenerating={isRegenerating}
            />
            <RecordRow
              type="CNAME"
              name={dnsSettings.routingCName}
              value={dnsSettings.routingCNameValue}
              status={dnsSettings.routingCNameStatus}
              error={dnsSettings.routingCNameError}
            />
          </tbody>
        </table>
      </div>

      <div className={styles.footerBand}>
        {checkFailureMessage ? (
          <div className={styles.footerFailure}>
            <Icon name="AlertCircle" size={16} className={styles.footerFailureIcon} />
            <span>
              <strong>Couldn&rsquo;t check your records.</strong> {checkFailureMessage}
            </span>
          </div>
        ) : (
          <span className={styles.footerNote}>
            {isAllVerified
              ? "The certificate renews automatically while both records stay in place."
              : "DNS changes usually appear within a few minutes and can take up to 48 hours. You can close this page — the domain activates on its own."}
          </span>
        )}
        <div className={styles.footerActions}>
          {checkFailureMessage && (
            <button
              type="button"
              className={styles.tryAgainBtn}
              onClick={onRefreshDnsStatus}
              disabled={isRefreshing}
            >
              {isRefreshing ? "Checking…" : "Try again"}
            </button>
          )}
          {disconnectButton}
        </div>
      </div>
    </div>
  );
};

export default DnsPanel;
